import { HomeLink } from "./HomeLink";
import { HomeAppStoreBadge } from "./HomeAppStoreBadge";
import { cn } from "@/lib/utils";

export { APP_STORE_URL } from "@/lib/appStore";

// The primary is deliberately a size step above the secondary: it was previously
// the SMALLER of the two (238x52 against 258x54 at 1440), so the alternative
// action out-weighed the main one in the fold.
const primary =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary px-9 py-4 text-lg font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
const secondary =
  "inline-flex items-center justify-center whitespace-nowrap rounded-full border border-border bg-transparent px-6 py-3 text-base font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";
const primaryDark = "!bg-[#F6EFE4] !text-[#1C130C]";
const secondaryDark =
  "!border-[rgba(246,239,228,.35)] !text-[#F6EFE4] hover:!bg-[rgba(246,239,228,.08)]";

/**
 * Device adaptive CTA pair, CSS only, no JavaScript and no user agent sniffing.
 *
 * The pair is rendered twice, each block already in its correct source order, so
 * visual order always equals DOM order and therefore focus order. Tailwind
 * `hidden` is `display: none`, so the inactive block is removed from both the tab
 * order and the accessibility tree. Never use `flex-row-reverse` here: it would
 * leave keyboard users tabbing in the opposite order to what they can see.
 *
 * Phone: an iPhone can install the app, so the App Store badge leads.
 * Desktop: an iOS app cannot be installed, so browsing leads and the badge is
 * the secondary. The badge is Apple's artwork and carries its own weight, so it
 * is never dressed as one of these pills.
 */
export function HomeCTAButtons({
  className,
  tone = "light",
}: {
  className?: string;
  tone?: "light" | "dark";
}) {
  // The closing section sits on the espresso surface, where `text-foreground`
  // and `border-border` are near invisible. Same markup, same order, same
  // destinations; only the two colour pairs change.
  const p = tone === "dark" ? primary + " " + primaryDark : primary;
  const s = tone === "dark" ? secondary + " " + secondaryDark : secondary;

  const browse = (cls: string) => (
    <HomeLink to="/browse" className={cls}>
      Browse cafés on the web
    </HomeLink>
  );

  return (
    <>
      {/* Phone: the App Store leads. */}
      <div className={cn("flex flex-col items-start gap-3 md:hidden", className)}>
        <HomeAppStoreBadge height={52} className="-ml-[5px]" />
        {browse(s)}
      </div>
      {/* Desktop and tablet: browsing leads. */}
      <div className={cn("hidden flex-wrap items-center gap-3 md:flex md:flex-row", className)}>
        {browse(p)}
        <HomeAppStoreBadge height={44} />
      </div>
    </>
  );
}
